import { useEffect, useState, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useApi } from '../api';
import ComplianceIndicator from '../components/ComplianceIndicator';
import ExpenseForm, { ExpenseData } from '../components/ExpenseForm';

interface Expense {
    id: string;
    date_of_service: string;
    provider: string;
    patient_name: string;
    amount: number;
    category: string;
    family_member: string;
    tax_year: number;
    reimbursement_status: string;
    irs_compliance_status: 'green' | 'yellow' | 'red';
    created_by_name?: string;
}

interface Document {
    id: string;
    document_type: string;
    file_name: string;
    file_url: string;
    uploaded_at: string;
}

const CATEGORIES = [
    { value: '', label: 'All Categories' },
    { value: 'medical', label: 'Medical' },
    { value: 'dental', label: 'Dental' },
    { value: 'vision', label: 'Vision' },
    { value: 'prescriptions', label: 'Prescriptions' },
    { value: 'equipment', label: 'Equipment' },
    { value: 'otc', label: 'Over-the-Counter' },
];

const COMPLIANCE_OPTIONS = [
    { value: '', label: 'All Status' },
    { value: 'green', label: '🟢 Documents Present' },
    { value: 'yellow', label: '🟡 Partial' },
    { value: 'red', label: '🔴 Needs Docs' },
];

// Format date as "Jan 15, 2026"
function formatDate(dateString: string): string {
    if (!dateString) return '-';
    const date = new Date(dateString + 'T00:00:00'); // Ensure local timezone
    return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
    });
}

export default function Expenses() {
    const [expenses, setExpenses] = useState<Expense[]>([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(true);
    const [searchParams, setSearchParams] = useSearchParams();
    const [editingExpense, setEditingExpense] = useState<Expense | null>(null);
    const [saving, setSaving] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [documents, setDocuments] = useState<Document[]>([]);
    const [loadingDocs, setLoadingDocs] = useState(false);
    const [uploadingDoc, setUploadingDoc] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const api = useApi();

    const filters = {
        search: searchParams.get('search') || '',
        category: searchParams.get('category') || '',
        compliance: searchParams.get('compliance') || '',
        tax_year: searchParams.get('tax_year') || '',
    };

    useEffect(() => {
        loadExpenses();
    }, [searchParams]);

    // Load documents when editing an expense
    useEffect(() => {
        if (editingExpense) {
            loadDocuments(editingExpense.id);
        } else {
            setDocuments([]);
        }
    }, [editingExpense]);

    async function loadExpenses() {
        setLoading(true);
        try {
            const params: Record<string, string> = {};
            if (filters.search) params.search = filters.search;
            if (filters.category) params.category = filters.category;
            if (filters.compliance) params.compliance = filters.compliance;
            if (filters.tax_year) params.tax_year = filters.tax_year;

            const result = await api.getExpenses(params);
            if (result.success && result.data) {
                const data = result.data as { expenses: Expense[]; total: number };
                setExpenses(data.expenses);
                setTotal(data.total);
            }
        } catch (error) {
            console.error('Failed to load expenses:', error);
        } finally {
            setLoading(false);
        }
    }

    async function loadDocuments(expenseId: string) {
        setLoadingDocs(true);
        try {
            const result = await api.getExpenseDocuments(expenseId);
            if (result.success && result.data) {
                setDocuments(result.data as Document[]);
            }
        } catch (error) {
            console.error('Failed to load documents:', error);
        } finally {
            setLoadingDocs(false);
        }
    }

    function updateFilter(key: string, value: string) {
        const newParams = new URLSearchParams(searchParams);
        if (value) {
            newParams.set(key, value);
        } else {
            newParams.delete(key);
        }
        setSearchParams(newParams);
    }

    async function handleSaveEdit(data: ExpenseData) {
        if (!editingExpense) return;
        setSaving(true);
        try {
            await api.updateExpense(editingExpense.id, data as unknown as Record<string, unknown>);
            setEditingExpense(null);
            await loadExpenses();
        } catch (error) {
            console.error('Failed to update expense:', error);
            alert('Failed to save changes');
        } finally {
            setSaving(false);
        }
    }

    async function handleDeleteExpense() {
        if (!editingExpense) return;
        if (!confirm(`Delete this expense from ${editingExpense.provider}? This cannot be undone.`)) {
            return;
        }
        setDeleting(true);
        try {
            await api.deleteExpense(editingExpense.id);
            setEditingExpense(null);
            await loadExpenses();
        } catch (error) {
            console.error('Failed to delete expense:', error);
            alert('Failed to delete expense');
        } finally {
            setDeleting(false);
        }
    }

    async function handleDeleteDocument(docId: string) {
        if (!confirm('Delete this document?')) return;
        try {
            await api.deleteDocument(docId);
            if (editingExpense) {
                await loadDocuments(editingExpense.id);
            }
        } catch (error) {
            console.error('Failed to delete document:', error);
            alert('Failed to delete document');
        }
    }

    async function handleUploadDocument(file: File) {
        if (!editingExpense) return;
        setUploadingDoc(true);
        try {
            await api.uploadDocument(editingExpense.id, file, 'receipt', false);
            await loadDocuments(editingExpense.id);
            await loadExpenses(); // Refresh to update compliance status
        } catch (error) {
            console.error('Failed to upload document:', error);
            alert('Failed to upload document');
        } finally {
            setUploadingDoc(false);
            if (fileInputRef.current) fileInputRef.current.value = '';
        }
    }

    // Generate tax year options (current year back to 10 years ago)
    const currentYear = new Date().getFullYear();
    const yearOptions = Array.from({ length: 11 }, (_, i) => currentYear - i);

    return (
        <div className="expenses-page">
            <header className="page-header">
                <h1>Expenses</h1>
                <div className="header-actions">
                    <Link to="/upload" className="btn btn-primary">
                        + Add Expense
                    </Link>
                </div>
            </header>

            {/* Filters */}
            <div className="filters-bar">
                <div className="search-box">
                    <input
                        type="search"
                        placeholder="Search provider, description..."
                        value={filters.search}
                        onChange={(e) => updateFilter('search', e.target.value)}
                        aria-label="Search expenses"
                    />
                </div>

                <select
                    value={filters.category}
                    onChange={(e) => updateFilter('category', e.target.value)}
                    aria-label="Filter by category"
                >
                    {CATEGORIES.map((cat) => (
                        <option key={cat.value} value={cat.value}>{cat.label}</option>
                    ))}
                </select>

                <select
                    value={filters.compliance}
                    onChange={(e) => updateFilter('compliance', e.target.value)}
                    aria-label="Filter by compliance status"
                >
                    {COMPLIANCE_OPTIONS.map((opt) => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                </select>

                <select
                    value={filters.tax_year}
                    onChange={(e) => updateFilter('tax_year', e.target.value)}
                    aria-label="Filter by tax year"
                >
                    <option value="">All Years</option>
                    {yearOptions.map((year) => (
                        <option key={year} value={year}>{year}</option>
                    ))}
                </select>
            </div>

            {/* Results count */}
            <div className="results-info">
                <span>{total} expense{total !== 1 ? 's' : ''} found</span>
            </div>

            {/* Expenses Table */}
            {loading ? (
                <div className="page-loading">
                    <div className="spinner"></div>
                </div>
            ) : expenses.length === 0 ? (
                <div className="empty-state">
                    <p>No expenses found</p>
                    <Link to="/upload" className="btn btn-primary">Upload your first receipt</Link>
                </div>
            ) : (
                <div className="expenses-table-wrapper">
                    <table className="expenses-table" role="grid">
                        <thead>
                            <tr>
                                <th scope="col">Status</th>
                                <th scope="col">Date</th>
                                <th scope="col">Provider</th>
                                <th scope="col">Description</th>
                                <th scope="col">Amount</th>
                                <th scope="col">Category</th>
                                <th scope="col">Year</th>
                                <th scope="col">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {expenses.map((expense) => (
                                <tr key={expense.id}>
                                    <td>
                                        <ComplianceIndicator status={expense.irs_compliance_status} />
                                    </td>
                                    <td>{formatDate(expense.date_of_service)}</td>
                                    <td>
                                        <Link to={`/expenses/${expense.id}`} className="expense-link">
                                            {expense.provider || 'Unknown'}
                                        </Link>
                                    </td>
                                    <td className="description-cell" title={expense.patient_name || ''}>
                                        {expense.patient_name
                                            ? (expense.patient_name.length > 40
                                                ? expense.patient_name.substring(0, 40) + '...'
                                                : expense.patient_name)
                                            : '-'}
                                    </td>
                                    <td className="amount-cell">${expense.amount.toFixed(2)}</td>
                                    <td>
                                        <span className={`category-badge category-${expense.category || 'none'}`}>
                                            {expense.category || '-'}
                                        </span>
                                    </td>
                                    <td>{expense.tax_year}</td>
                                    <td className="actions-cell">
                                        <button
                                            onClick={() => setEditingExpense(expense)}
                                            className="btn btn-sm btn-secondary"
                                            title="Quick Edit"
                                        >
                                            ✏️
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {/* Edit Modal */}
            {editingExpense && (
                <div className="modal-overlay" onClick={() => setEditingExpense(null)}>
                    <div className="modal-content modal-lg" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h2>Edit Expense</h2>
                            <button
                                className="modal-close"
                                onClick={() => setEditingExpense(null)}
                                aria-label="Close modal"
                            >
                                ×
                            </button>
                        </div>
                        <div className="modal-body">
                            <ExpenseForm
                                expense={editingExpense}
                                onSave={handleSaveEdit}
                                onCancel={() => setEditingExpense(null)}
                                saving={saving}
                                descriptionLabel="Description"
                            />

                            {/* Documents Section */}
                            <div className="modal-section">
                                <h3>Attachments</h3>
                                {loadingDocs ? (
                                    <p style={{ color: 'var(--text-muted)', fontSize: '0.875rem' }}>Loading...</p>
                                ) : (
                                    <>
                                        {documents.length === 0 ? (
                                            <p style={{ color: 'var(--text-muted)', fontSize: '0.875rem' }}>No attachments</p>
                                        ) : (
                                            <div className="documents-list">
                                                {documents.map((doc) => (
                                                    <div key={doc.id} className="document-item">
                                                        <a href={doc.file_url} target="_blank" rel="noopener noreferrer">
                                                            📄 {doc.file_name}
                                                        </a>
                                                        <button
                                                            className="btn btn-sm btn-danger"
                                                            onClick={() => handleDeleteDocument(doc.id)}
                                                            title="Delete document"
                                                        >
                                                            🗑️
                                                        </button>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                        <div style={{ marginTop: '0.75rem' }}>
                                            <input
                                                ref={fileInputRef}
                                                type="file"
                                                accept="image/*,.pdf"
                                                onChange={(e) => e.target.files?.[0] && handleUploadDocument(e.target.files[0])}
                                                style={{ display: 'none' }}
                                            />
                                            <button
                                                className="btn btn-secondary btn-sm"
                                                onClick={() => fileInputRef.current?.click()}
                                                disabled={uploadingDoc}
                                            >
                                                {uploadingDoc ? 'Uploading...' : '📎 Add Attachment'}
                                            </button>
                                        </div>
                                    </>
                                )}
                            </div>

                            {/* Delete Button */}
                            <div className="modal-section danger-zone">
                                <button
                                    className="btn btn-danger"
                                    onClick={handleDeleteExpense}
                                    disabled={deleting}
                                >
                                    {deleting ? 'Deleting...' : '🗑️ Delete Expense'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
